import { FiArrowDown, FiArrowUp } from "react-icons/fi";

import { flowArrow, formatCurrency } from "@/utils/formatters";

export interface FlowSummaryProps {
  /** Rótulo en versalitas, p. ej. "Flujo de septiembre". */
  label: string;
  /** Neto del periodo con signo (negativo = gastas más de lo que ingresas). */
  net: number;
  /** Ingresos del periodo (magnitud). */
  income: number;
  /** Gastos del periodo (magnitud). */
  expense: number;
  /** Texto de la tarjeta de ingresos, p. ej. "Ingresos". */
  incomeLabel: string;
  /** Texto de la tarjeta de gastos, p. ej. "Gastos". */
  expenseLabel: string;
}

/** Flujo del periodo: neto grande con flecha y dos tarjetas de color (ingresos lima, gastos flame). */
export function FlowSummary({ label, net, income, expense, incomeLabel, expenseLabel }: FlowSummaryProps) {
  const netArrow = flowArrow(net < 0 ? "expense" : "income");
  const netSign = net < 0 ? "-" : "";
  const netText = `${netSign}${formatCurrency(Math.abs(net))}`;

  return (
    <section aria-label={label} className="flex flex-col gap-4 text-[var(--ds-ink)] md:flex-row md:items-center md:gap-7">
      <div className="min-w-0 md:text-right">
        <p className="m-0 text-[11px] font-medium uppercase leading-[14px] tracking-[0.18em] text-[var(--ds-ink-muted)]">{label}</p>
        <p className="m-0 mt-2 whitespace-nowrap font-[var(--ds-font-display)] text-[40px] font-medium leading-none tracking-[-0.04em] md:text-[48px]">
          <span aria-hidden="true">{netArrow} </span>
          {netText}
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3 md:w-[400px] md:flex-none">
        <div role="group" aria-label={`${incomeLabel} ${formatCurrency(income)}`} className="flex min-w-0 items-center gap-3 rounded-[var(--ds-radius-lg)] bg-[var(--ds-card-lime)] p-4 text-[var(--ds-on-card)]">
          <span aria-hidden="true" className="flex size-10 flex-none items-center justify-center rounded-full bg-[var(--ds-on-card)] text-[var(--ds-card-lime)]">
            <FiArrowUp className="size-4" />
          </span>
          <span className="flex min-w-0 flex-col">
            <span className="text-xs leading-4">{incomeLabel}</span>
            <strong className="truncate font-[var(--ds-font-display)] text-[22px] font-medium leading-7 tracking-[-0.02em]">{formatCurrency(income)}</strong>
          </span>
        </div>
        <div role="group" aria-label={`${expenseLabel} ${formatCurrency(expense)}`} className="flex min-w-0 items-center gap-3 rounded-[var(--ds-radius-lg)] bg-[var(--ds-card-flame)] p-4 text-[var(--ds-on-card)]">
          <span aria-hidden="true" className="flex size-10 flex-none items-center justify-center rounded-full bg-[var(--ds-on-card)] text-[var(--ds-card-flame)]">
            <FiArrowDown className="size-4" />
          </span>
          <span className="flex min-w-0 flex-col">
            <span className="text-xs leading-4">{expenseLabel}</span>
            <strong className="truncate font-[var(--ds-font-display)] text-[22px] font-medium leading-7 tracking-[-0.02em]">{formatCurrency(expense)}</strong>
          </span>
        </div>
      </div>
    </section>
  );
}
